export const PROPERTY_VALUE_LOOKUP_VERSION = "property-market-value-v1";
export const ZILLOW_ZESTIMATE_INPUT_VERSION = "zillow-zestimate-input-v1";
export const ZILLOW_ZESTIMATE_POLICY_VERSION = "zillow-zestimate-policy-v1";

export type ZillowValueToolInput = {
  workItemId: string;
  address: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
  };
};

export type ZillowValueObservation = {
  marketValue: number;
  requestedAddressDisplay: string;
  comparisonKey: string;
  providerJobId: string | null;
  observedAt: string | null;
};

// Adapter version is evidence metadata and a freshness/version rule, not the
// address-level cache identity. Stale or off-version observations stay as
// history; policy decides reuse vs refresh.
// v14: v13 plus source-observed numeric/word ordinal street equivalence.
// Both rules remain inside an otherwise exact street identity.
// A USPS-equivalent candidate may route directly to its ZPID. Results-page
// routes are never sent through Browser API because the provider blocks those
// paths under robots policy. Persistent verification still receives one fresh
// session retry before terminal review.
export const ZILLOW_PUBLIC_WEB_PROVIDER_VERSION = "zillow-public-web-v14";

export const PROPERTY_VALUE_FAILURE_CLASSES = [
  "property_address_invalid",
  "property_value_no_match",
  "property_value_ambiguous_match",
  "property_value_address_mismatch",
  /** Property identity confirmed; Zillow shows an active/pending listing (not a Zestimate). */
  "property_found_active_listing",
  /** Property identity confirmed; Zillow has no usable market value (Zestimate $-- / not available). */
  "property_found_value_unavailable",
  "property_value_provider_account_suspended",
  "property_value_provider_ip_blocked",
  "property_value_provider_blocked",
  "property_value_provider_changed",
  "property_value_timeout",
  "property_value_artifact_failed",
  "property_value_unexpected"
] as const;

export type PropertyValueFailureClass = (typeof PROPERTY_VALUE_FAILURE_CLASSES)[number];

export type PropertyAddressInput = {
  fullAddress?: unknown;
  street?: unknown;
  unit?: unknown;
  city?: unknown;
  state?: unknown;
  postalCode?: unknown;
};

/**
 * Jurisdictions accepted by the property-value provider contract. The postal
 * code is the canonical downstream representation; names are accepted only at
 * input boundaries and never copied into cache or comparison identities.
 */
export const PROPERTY_VALUE_JURISDICTIONS = [
  ["AL", "Alabama"],
  ["AK", "Alaska"],
  ["AZ", "Arizona"],
  ["AR", "Arkansas"],
  ["CA", "California"],
  ["CO", "Colorado"],
  ["CT", "Connecticut"],
  ["DE", "Delaware"],
  ["FL", "Florida"],
  ["GA", "Georgia"],
  ["HI", "Hawaii"],
  ["ID", "Idaho"],
  ["IL", "Illinois"],
  ["IN", "Indiana"],
  ["IA", "Iowa"],
  ["KS", "Kansas"],
  ["KY", "Kentucky"],
  ["LA", "Louisiana"],
  ["ME", "Maine"],
  ["MD", "Maryland"],
  ["MA", "Massachusetts"],
  ["MI", "Michigan"],
  ["MN", "Minnesota"],
  ["MS", "Mississippi"],
  ["MO", "Missouri"],
  ["MT", "Montana"],
  ["NE", "Nebraska"],
  ["NV", "Nevada"],
  ["NH", "New Hampshire"],
  ["NJ", "New Jersey"],
  ["NM", "New Mexico"],
  ["NY", "New York"],
  ["NC", "North Carolina"],
  ["ND", "North Dakota"],
  ["OH", "Ohio"],
  ["OK", "Oklahoma"],
  ["OR", "Oregon"],
  ["PA", "Pennsylvania"],
  ["RI", "Rhode Island"],
  ["SC", "South Carolina"],
  ["SD", "South Dakota"],
  ["TN", "Tennessee"],
  ["TX", "Texas"],
  ["UT", "Utah"],
  ["VT", "Vermont"],
  ["VA", "Virginia"],
  ["WA", "Washington"],
  ["WV", "West Virginia"],
  ["WI", "Wisconsin"],
  ["WY", "Wyoming"],
  ["DC", "District of Columbia"],
  ["AS", "American Samoa"],
  ["GU", "Guam"],
  ["MP", "Northern Mariana Islands"],
  ["PR", "Puerto Rico"],
  ["VI", "U.S. Virgin Islands"]
] as const;

export type PropertyValueJurisdictionCode = (typeof PROPERTY_VALUE_JURISDICTIONS)[number][0];

const PROPERTY_VALUE_JURISDICTION_CODE_BY_INPUT = new Map<string, PropertyValueJurisdictionCode>([
  ...PROPERTY_VALUE_JURISDICTIONS.flatMap(([code, name]) => [[code, code], [name.toUpperCase(), code]] as const),
  ["US VIRGIN ISLANDS", "VI"],
  ["UNITED STATES VIRGIN ISLANDS", "VI"],
  ["VIRGIN ISLANDS", "VI"]
]);

/** Converts a full jurisdiction name or postal code to its canonical code. */
export function normalizePropertyState(value: unknown): PropertyValueJurisdictionCode | null {
  return PROPERTY_VALUE_JURISDICTION_CODE_BY_INPUT.get(normalizedText(value).toUpperCase()) ?? null;
}

export type NormalizedPropertyAddress = {
  submitted: string;
  street: string;
  unit: string | null;
  city: string;
  state: string;
  postalCode: string;
  display: string;
  comparisonKey: string;
};

/**
 * Converts either a complete US address or structured address fields into the
 * one canonical property identity used by Toolbench, the browser worker, and
 * Google Sheets. The submitted text remains available for evidence receipts.
 */
export function normalizePropertyAddress(input: PropertyAddressInput): NormalizedPropertyAddress | null {
  const fullAddress = normalizedText(input.fullAddress);
  const structured = normalizeStructuredAddress(input);
  if (structured) return structured;
  if (!fullAddress) return null;
  return parseFullPropertyAddress(fullAddress);
}

export function parseFullPropertyAddress(value: string): NormalizedPropertyAddress | null {
  const submitted = normalizedText(value);
  if (!submitted || submitted.length > 240) return null;
  const withoutCountry = submitted.replace(/,?\s+(?:USA|United States(?: of America)?)$/i, "").trim();
  const match = /^(.*?),\s*([^,]+),\s*([A-Za-z][A-Za-z .'-]{1,79})\s+(\d{5}(?:-\d{4})?)$/.exec(withoutCountry);
  if (!match) return null;
  return buildNormalizedAddress({
    submitted,
    street: match[1] ?? "",
    unit: "",
    city: match[2] ?? "",
    state: match[3] ?? "",
    postalCode: match[4] ?? ""
  });
}

export function samePropertyAddress(left: NormalizedPropertyAddress, right: NormalizedPropertyAddress): boolean {
  if (left.comparisonKey === right.comparisonKey) return true;
  if (
    identityComparisonPart(left.city) !== identityComparisonPart(right.city) ||
    left.state !== right.state
  ) return false;
  let leftStreet = propertyStreetIdentity(left);
  let rightStreet = propertyStreetIdentity(right);
  if (!leftStreet || !rightStreet) return false;
  if (!samePropertyUnit(leftStreet, rightStreet)) {
    // County exports can flatten an alphabetic unit immediately after the
    // house number (`304 B Monroe Street`) while Zillow returns the exact
    // source record as `304 Monroe St #B`. Reinterpret that token only when
    // the other source explicitly supplies the same unit.
    const leftAlternative = leadingFlattenedUnitIdentity(left, rightStreet.unit);
    const rightAlternative = leadingFlattenedUnitIdentity(right, leftStreet.unit);
    if (leftAlternative && samePropertyUnit(leftAlternative, rightStreet)) leftStreet = leftAlternative;
    else if (rightAlternative && samePropertyUnit(leftStreet, rightAlternative)) rightStreet = rightAlternative;
    else return false;
  }
  if (!sameStreetCore(leftStreet.core, rightStreet.core)) return false;
  if (leftStreet.directions.join("|") !== rightStreet.directions.join("|")) {
    // Zillow may omit an N/S/E/W direction from an otherwise exact USPS
    // property record. Allow only the missing-vs-present case; two conflicting
    // explicit directions are still different properties.
    const oneDirectionMissing = leftStreet.directions.length === 0 || rightStreet.directions.length === 0;
    if (!oneDirectionMissing) return false;
  }
  // County feeds sometimes carry a stale ZIP while Zillow returns the exact
  // house/street/city/state with the current postal code. Street, unit, city,
  // and state remain the identity boundary; a ZIP disagreement alone is not
  // allowed to discard that otherwise exact source-backed property.
  // Source lists sometimes omit the generic street type while Zillow supplies
  // it. A conflicting pair (Lane vs Place) is not equivalent, but one missing
  // suffix may still be the same exact house/street/city/ZIP identity.
  return !leftStreet.suffix || !rightStreet.suffix || leftStreet.suffix === rightStreet.suffix;
}

/**
 * Validates a single provisional Zillow discovery candidate before its ZPID
 * is sent to the existing detail-page validator. The provider may label the
 * same postal address with a different city, omit a directional component,
 * omit a unit the source carried, or use a different generic street suffix
 * (Street vs Avenue) after Tier-2 dropped the suffix from the query. It may
 * not change the house number, state, ZIP, or street-name core. This is
 * intentionally not the normal property identity contract: the detail page
 * remains authoritative for the final city/address/unit decision.
 */
export function samePropertyAddressForZillowDiscoveryCandidate(
  candidate: NormalizedPropertyAddress,
  requested: NormalizedPropertyAddress
): boolean {
  if (
    candidate.state !== requested.state ||
    candidate.postalCode.slice(0, 5) !== requested.postalCode.slice(0, 5)
  ) return false;
  const candidateStreet = propertyStreetIdentity(candidate);
  const requestedStreet = propertyStreetIdentity(requested);
  if (!candidateStreet || !requestedStreet) return false;
  if (candidateStreet.core[0] !== requestedStreet.core[0]) return false;
  if (!samePropertyUnitForZillowDiscovery(candidateStreet, requestedStreet)) return false;
  if (!sameStreetCoreForZillowDiscovery(
    zillowDiscoveryStreetCore(candidate, candidateStreet.core),
    zillowDiscoveryStreetCore(requested, requestedStreet.core)
  )) return false;
  // A provider may omit a source direction, or add a complementary one the
  // source left off (e.g. source "…Forest Dr SE" vs Zillow "N …Forest Dr SE").
  // Reject only when both sides supply directions and they share none —
  // that is the conflicting-direction case (N vs S on the same axis).
  const requestedDirections = new Set(requestedStreet.directions);
  const candidateDirections = new Set(candidateStreet.directions);
  // An explicit direction introduced by the provider is not a safe
  // provisional match when the source address supplied none. It can select a
  // different street segment or property; only a provider omission or a
  // direction that is explicitly shared with the source is admissible.
  if (!requestedDirections.size && candidateDirections.size) return false;
  if (requestedDirections.size && candidateDirections.size) {
    const sharesDirection = [...requestedDirections].some((direction) => candidateDirections.has(direction));
    if (!sharesDirection) return false;
  }
  // Generic suffix conflicts (ST vs AVE, RD vs LN) are allowed only for this
  // provisional hop. Exact samePropertyAddress still rejects them after the
  // detail page is fetched, unless the page title/payload itself confirms.
  return true;
}

function samePropertyUnitForZillowDiscovery(
  left: NonNullable<ReturnType<typeof propertyStreetIdentity>>,
  right: NonNullable<ReturnType<typeof propertyStreetIdentity>>
): boolean {
  if (samePropertyUnit(left, right)) return true;
  // Source lists often keep a unit token (`#A`, `Unit A`) while Zillow's
  // autocomplete card for the same house omits it. Allow only the
  // provider-missing-unit case; a provider that invents a conflicting unit
  // remains rejected until the detail page can disambiguate.
  const requestedHasUnit = right.unit !== null;
  const candidateHasUnit = left.unit !== null;
  if (requestedHasUnit && !candidateHasUnit) return true;
  if (!requestedHasUnit && candidateHasUnit) return left.unit === right.core[0] || right.unit === left.core[0];
  return false;
}

function sameStreetCore(left: string[], right: string[]): boolean {
  if (left.join("|") === right.join("|")) return true;
  if (left.length !== right.length || left[0] !== right[0]) return false;
  let silentEDifferenceCount = 0;
  for (let index = 1; index < left.length; index += 1) {
    const leftToken = left[index]!;
    const rightToken = right[index]!;
    if (leftToken === rightToken) continue;
    const [shorter, longer] = leftToken.length < rightToken.length
      ? [leftToken, rightToken]
      : [rightToken, leftToken];
    if (shorter.length < 4 || longer !== `${shorter}E`) return false;
    silentEDifferenceCount += 1;
  }
  return silentEDifferenceCount === 1;
}

/** Discovery-only: silent-E plus one simple plural (Estate/Estates). */
function sameStreetCoreForZillowDiscovery(left: string[], right: string[]): boolean {
  if (sameStreetCore(left, right)) return true;
  if (left.length !== right.length || left[0] !== right[0]) return false;
  let softDifferenceCount = 0;
  for (let index = 1; index < left.length; index += 1) {
    const leftToken = left[index]!;
    const rightToken = right[index]!;
    if (leftToken === rightToken) continue;
    if (isSilentTerminalEVariant(leftToken, rightToken) || isSimplePluralStreetTokenVariant(leftToken, rightToken)) {
      softDifferenceCount += 1;
      if (softDifferenceCount > 1) return false;
      continue;
    }
    return false;
  }
  return softDifferenceCount === 1;
}

function isSilentTerminalEVariant(left: string, right: string): boolean {
  const [shorter, longer] = left.length < right.length ? [left, right] : [right, left];
  return shorter.length >= 4 && longer === `${shorter}E`;
}

function isSimplePluralStreetTokenVariant(left: string, right: string): boolean {
  const [shorter, longer] = left.length < right.length ? [left, right] : [right, left];
  if (shorter.length < 4) return false;
  // Require 5+ chars so HOLCOMB≠HOLCOMBS while ESTATE=ESTATES.
  if (shorter.length < 5) return false;
  if (longer === `${shorter}S` && !shorter.endsWith("S")) return true;
  if (shorter.endsWith("Y") && longer === `${shorter.slice(0, -1)}IES`) return true;
  return false;
}

function zillowDiscoveryStreetCore(
  address: NormalizedPropertyAddress,
  core: string[]
): string[] {
  // Zillow's `Rdg` spelling is a terminal street-name alias only. Do not
  // rewrite an interior name such as `Ridge View`, where the two streets
  // remain distinct after the generic `View` suffix is removed.
  const terminalToken = address.street.toUpperCase().replace(/[^A-Z0-9]+$/g, "").match(/[A-Z0-9]+$/)?.[0] ?? "";
  if (terminalToken !== "RIDGE" && terminalToken !== "RDG") return core;
  return core.map((token, index) => index === core.length - 1 && token === "RDG" ? "RIDGE" : token);
}

function leadingFlattenedUnitIdentity(
  address: NormalizedPropertyAddress,
  expectedUnit: string | null
): NonNullable<ReturnType<typeof propertyStreetIdentity>> | null {
  if (address.unit || !expectedUnit || !/^[A-Z]$/.test(expectedUnit)) return null;
  const match = /^(\d+[A-Z]?)\s+([A-Z])\s+(.+)$/i.exec(address.street);
  if (!match?.[1] || !match[2] || !match[3] || identityComparisonPart(match[2]) !== expectedUnit) return null;
  const restructured = normalizePropertyAddress({
    street: `${match[1]} ${match[3]}`,
    unit: match[2],
    city: address.city,
    state: address.state,
    postalCode: address.postalCode
  });
  return restructured ? propertyStreetIdentity(restructured) : null;
}

function samePropertyUnit(
  left: NonNullable<ReturnType<typeof propertyStreetIdentity>>,
  right: NonNullable<ReturnType<typeof propertyStreetIdentity>>
): boolean {
  if (left.unit === right.unit) return true;
  // Some single-unit Zillow records repeat the house number as a unit (for
  // example, "608 Angler Way #608") while the county source carries only the
  // street address. Treat only that redundant provider token as equivalent;
  // a distinct or conflicting unit remains a different property.
  const [missing, present, houseNumber] = left.unit === null
    ? [left.unit, right.unit, left.core[0]]
    : [right.unit, left.unit, right.core[0]];
  return missing === null && present !== null && present === houseNumber;
}

export function propertyAddressComparisonKey(input: PropertyAddressInput): string | null {
  return normalizePropertyAddress(input)?.comparisonKey ?? null;
}

function normalizeStructuredAddress(input: PropertyAddressInput): NormalizedPropertyAddress | null {
  const street = normalizedText(input.street);
  const unit = normalizedText(input.unit);
  const city = normalizedText(input.city);
  const state = normalizedText(input.state);
  const postalCode = normalizedText(input.postalCode);
  if (!street && !city && !state && !postalCode && !unit) return null;
  return buildNormalizedAddress({
    submitted: [street, unit, city, [state, postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", "),
    street,
    unit,
    city,
    state,
    postalCode
  });
}

function buildNormalizedAddress(input: {
  submitted: string;
  street: string;
  unit: string;
  city: string;
  state: string;
  postalCode: string;
}): NormalizedPropertyAddress | null {
  const street = normalizedText(input.street);
  const unit = normalizedText(input.unit);
  const city = normalizedText(input.city);
  const state = normalizePropertyState(input.state);
  const postalCode = normalizedText(input.postalCode);
  if (!street || !/^\d+[A-Za-z]?(?:[-\s]+|\s*[A-Za-z])/.test(street)) return null;
  if (!city || !/^[A-Za-z][A-Za-z .'-]{1,79}$/.test(city)) return null;
  if (!state) return null;
  if (!/^\d{5}(?:-\d{4})?$/.test(postalCode)) return null;
  if (unit && unit.length > 40) return null;
  const streetWithUnit = [street, unit].filter(Boolean).join(" ");
  const display = `${streetWithUnit}, ${city}, ${state} ${postalCode}`;
  const comparisonKey = [
    streetComparisonPart(streetWithUnit),
    identityComparisonPart(city),
    identityComparisonPart(state),
    identityComparisonPart(postalCode.slice(0, 5))
  ].join("|");
  return {
    submitted: normalizedText(input.submitted),
    street,
    unit: unit || null,
    city,
    state,
    postalCode,
    display,
    comparisonKey
  };
}

function streetComparisonPart(value: string): string {
  const suffixes: Record<string, string> = {
    STREET: "ST",
    ROAD: "RD",
    DRIVE: "DR",
    AVENUE: "AVE",
    BOULEVARD: "BLVD",
    LANE: "LN",
    COURT: "CT",
    CIRCLE: "CIR",
    HIGHWAY: "HWY",
    PARKWAY: "PKWY",
    PLACE: "PL",
    TERRACE: "TER",
    TRAIL: "TRL",
    HOLLOW: "HOLW",
    WAY: "WAY"
  };
  return value
    .toUpperCase()
    // Zillow commonly abbreviates a displayed USPS address.  These are
    // component-level normalizations only; house number, city, state, ZIP,
    // and the remaining street identity still have to match exactly.
    .replace(/\bNORTH\s*WEST\b|\bNORTHWEST\b/g, "NW")
    .replace(/\bNORTH\s*EAST\b|\bNORTHEAST\b/g, "NE")
    .replace(/\bSOUTH\s*WEST\b|\bSOUTHWEST\b/g, "SW")
    .replace(/\bSOUTH\s*EAST\b|\bSOUTHEAST\b/g, "SE")
    .replace(/\bNORTH\b/g, "N")
    .replace(/\bSOUTH\b/g, "S")
    .replace(/\bEAST\b/g, "E")
    .replace(/\bWEST\b/g, "W")
    .replace(/\bSHORT\s+CUT\b/g, "SHORTCUT")
    // North Carolina road data may call the same state route either “State
    // Highway” or “NC Highway”; a cardinal prefix in front of that route is
    // a directional USPS component, not a distinct road name.
    .replace(/\b(?:N|S|E|W)\s+NC\s+HIGHWAY\b/g, "NC HIGHWAY")
    .replace(/\bSTATE\s+HIGHWAY\b/g, "NC HIGHWAY")
    .replace(/\b(STREET|ROAD|DRIVE|AVENUE|BOULEVARD|LANE|COURT|CIRCLE|HIGHWAY|PARKWAY|PLACE|TERRACE|TRAIL|HOLLOW|WAY)\b/g, (suffix) => suffixes[suffix] ?? suffix)
    .replace(/[^A-Z0-9]/g, "");
}

export function propertyStreetIdentity(address: NormalizedPropertyAddress): {
  core: string[];
  directions: string[];
  suffix: string | null;
  unit: string | null;
  numberedRoute: boolean;
} | null {
  const rawStreet = [address.street, address.unit].filter(Boolean).join(" ").toUpperCase();
  const routeNumberUsesHash = /\b(?:HIGHWAY|HWY|ROUTE|RTE)\b[^#]*#[A-Z0-9-]+\b/i.test(rawStreet);
  const labeledInlineUnit = routeNumberUsesHash
    ? null
    : /(?:#|\bAPT\b|\bAPARTMENT\b|\bUNIT\b|\bSUITE\b|\bSTE\b)\s*([A-Z0-9-]+)\b/i.exec(rawStreet)?.[1] ?? null;
  // County exports often flatten a condo unit into the street cell without
  // `#` (for example, `435 South Tryon Street 502`). Interpret only a final
  // numeric token immediately after a non-route street suffix. The unit still
  // has to match Zillow's explicit unit, so this cannot collapse two condos.
  const unlabeledNumericUnit = routeNumberUsesHash || labeledInlineUnit
    ? null
    : /\b(?:STREET|ST|ROAD|RD|DRIVE|DR|AVENUE|AVE|BOULEVARD|BLVD|LANE|LN|COURT|CT|CIRCLE|CIR|PARKWAY|PKWY|PLACE|PL|TERRACE|TER|TRAIL|TRL|HOLLOW|HOLW|VIEW|VW|WAY)\s+(\d{1,6})$/.exec(rawStreet)?.[1] ?? null;
  // Some county feeds flatten an alphanumeric condominium unit after the
  // street suffix (`103 Marbury Court 1C`) instead of using `#1C` or a
  // separate unit column. Treat only a final alphanumeric token after a
  // recognized non-route suffix as a unit; route names such as `NC 16
  // Business Highway` must remain intact.
  const unlabeledAlphaNumericUnit = routeNumberUsesHash || labeledInlineUnit || unlabeledNumericUnit
    ? null
    : /\b(?:STREET|ST|ROAD|RD|DRIVE|DR|AVENUE|AVE|BOULEVARD|BLVD|LANE|LN|COURT|CT|CIRCLE|CIR|PARKWAY|PKWY|PLACE|PL|TERRACE|TER|TRAIL|TRL|HOLLOW|HOLW|VIEW|VW|WAY)\s+([A-Z]\d{0,5}|\d+[A-Z][A-Z0-9-]*)$/.exec(rawStreet)?.[1] ?? null;
  const inlineUnit = labeledInlineUnit ?? unlabeledNumericUnit ?? unlabeledAlphaNumericUnit;
  const unit = identityComparisonPart(address.unit ?? inlineUnit ?? "") || null;
  const withoutUnit = labeledInlineUnit
    ? rawStreet.replace(/(?:#|\bAPT\b|\bAPARTMENT\b|\bUNIT\b|\bSUITE\b|\bSTE\b)\s*[A-Z0-9-]+\b/i, " ")
    : unlabeledNumericUnit
      ? rawStreet.replace(/\s+\d{1,6}$/, " ")
      : unlabeledAlphaNumericUnit
        ? rawStreet.replace(/\s+(?:[A-Z]\d{0,5}|\d+[A-Z][A-Z0-9-]*)$/i, " ")
        : rawStreet;
  // One county export abbreviated a terminal `Avenue` as a lone `A`. Treat
  // that token as a suffix only when no recognized street suffix precedes it;
  // `Main Street A` therefore remains distinct and cannot lose an unlabeled
  // unit designator.
  const terminalAvenueExpanded = /\s+A$/i.test(withoutUnit)
    && !/\b(?:STREET|ST|ROAD|RD|DRIVE|DR|AVENUE|AVE|BOULEVARD|BLVD|LANE|LN|COURT|CT|CIRCLE|CIR|HIGHWAY|HWY|PARKWAY|PKWY|PLACE|PL|TERRACE|TER|TRAIL|TRL|HOLLOW|HOLW|VIEW|VW|WAY)\s+A$/i.test(withoutUnit)
    ? withoutUnit.replace(/\s+A$/i, " AVE")
    : withoutUnit;
  const normalized = terminalAvenueExpanded
    .replace(/\b(?:FIRST|SECOND|THIRD|FOURTH|FIFTH|SIXTH|SEVENTH|EIGHTH|NINTH|TENTH)\b/g, (ordinal) => ({
      FIRST: "1ST",
      SECOND: "2ND",
      THIRD: "3RD",
      FOURTH: "4TH",
      FIFTH: "5TH",
      SIXTH: "6TH",
      SEVENTH: "7TH",
      EIGHTH: "8TH",
      NINTH: "9TH",
      TENTH: "10TH"
    })[ordinal] ?? ordinal)
    .replace(/\bNORTH\s*WEST\b|\bNORTHWEST\b/g, "NW")
    .replace(/\bNORTH\s*EAST\b|\bNORTHEAST\b/g, "NE")
    .replace(/\bSOUTH\s*WEST\b|\bSOUTHWEST\b/g, "SW")
    .replace(/\bSOUTH\s*EAST\b|\bSOUTHEAST\b/g, "SE")
    .replace(/\bNORTH\b/g, "N")
    .replace(/\bSOUTH\b/g, "S")
    .replace(/\bEAST\b/g, "E")
    .replace(/\bWEST\b/g, "W")
    .replace(/\bSHORT\s+CUT\b/g, "SHORTCUT")
    .replace(/\bSTATE\s+HIGHWAY\b/g, "NC HWY")
    .replace(/\bHIGHWAY\b/g, "HWY")
    // NC route feeds place the jurisdiction marker before the route number
    // (`NC 16`, `Old NC 109`). Zillow may render the same road as State
    // Highway / Highway; the numeric route and any Old/Business component
    // remain part of the exact identity.
    .replace(/\bNC(?=\s+\d)/g, "")
    // Zillow sometimes shortens an NC state-route display from “NC Highway
    // 343” to “Hwy 343” after autocomplete has already resolved the exact
    // ZPID. Within an NC address, the jurisdiction marker is not part of the
    // street identity; the house number, route number, direction, city, state,
    // and unit still have to match.
    .replace(/\bNC\s+HWY\b/g, "HWY")
    // County lists also abbreviate a concurrent state route as “NC 24-27”
    // while Zillow renders “State Highway 24 #27”. Remove only the NC token
    // immediately after the house number and immediately before a numeric
    // route; ordinary street-name tokens remain untouched.
    .replace(/^(\d+[A-Z]?)\s+NC(?=\s+\d)/, "$1")
    .replace(/\bSTREET\b/g, "ST")
    .replace(/\bROAD\b/g, "RD")
    .replace(/\bDRIVE\b/g, "DR")
    .replace(/\bAVENUE\b/g, "AVE")
    .replace(/\bBOULEVARD\b/g, "BLVD")
    .replace(/\bLANE\b/g, "LN")
    .replace(/\bCOURT\b/g, "CT")
    .replace(/\bCIRCLE\b/g, "CIR")
    .replace(/\bPARKWAY\b/g, "PKWY")
    .replace(/\bPLACE\b/g, "PL")
    .replace(/\bTERRACE\b/g, "TER")
    .replace(/\bTRAIL\b/g, "TRL")
    .replace(/\bHOLLOW\b/g, "HOLW")
    .replace(/\bVIEW\b/g, "VW")
    .replace(/\bWAY\b/g, "WAY");
  const tokens = normalized.match(/[A-Z0-9]+/g) ?? [];
  if (!tokens.length || !/^\d+[A-Z]?$/.test(tokens[0] ?? "")) return null;
  const directionSet = new Set(["N", "S", "E", "W", "NE", "NW", "SE", "SW"]);
  const suffixSet = new Set(["ST", "RD", "DR", "AVE", "BLVD", "LN", "CT", "CIR", "HWY", "PKWY", "PL", "TER", "TRL", "HOLW", "VW", "WAY"]);
  const directions = [...new Set(tokens.filter((token) => directionSet.has(token)))].sort();
  const suffixTokens = tokens.filter((token) => suffixSet.has(token));
  const suffix = suffixTokens.length ? suffixTokens.at(-1)! : null;
  const core = tokens.filter((token) => !directionSet.has(token) && !suffixSet.has(token));
  const numberedRoute = suffix === "HWY" && core.slice(1).some((token) => /^\d+(?:-\d+)?$/.test(token));
  return { core, directions, suffix, unit, numberedRoute };
}

function identityComparisonPart(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function normalizedText(value: unknown): string {
  return value == null ? "" : String(value).replaceAll(/\s+/g, " ").trim();
}
